import React from "react";
import { useParams } from "react-router-dom";

const UPDATED = "June 2026";

const CONTENT = {
  privacy: {
    title: "Privacy Policy",
    intro:
      'This Privacy Policy explains how Metros Franchise ("we", "us") collects, uses and protects your personal information when you use our takeaway ordering apps ("the App"). We operate from Metros Epsom, 100 Chessington Road, Epsom KT19 9UR, United Kingdom. We comply with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.',
    sections: [
      {
        h: "1. Who is the data controller?",
        p: ["Metros Franchise, Metros Epsom, 100 Chessington Road, Epsom KT19 9UR, United Kingdom. For any privacy question or to exercise your rights below, email Saiyeshaa4@gmail.com."],
      },
      {
        h: "2. What personal data we collect",
        list: [
          "Account data: your name, email, phone number and password (stored as a one-way hash — we never see your password).",
          "Order data: your order history, items, notes, delivery/collection choice and receipt.",
          "Delivery data: the delivery address you enter and its geocoded coordinates so our driver can reach you.",
          "Driver location (drivers only): approximate GPS location while a driver is on shift, used to route deliveries and show live ETAs to customers.",
          "Chat messages: messages exchanged between customer, driver and shop about an order.",
          "Technical data: device type, app version, session tokens (stored securely in your device keychain) and error logs used to keep the App running.",
        ],
        p2: ["We do not collect or store card numbers or bank details. All payments are taken on delivery/collection."],
      },
      {
        h: "3. Why we collect it (lawful bases)",
        list: [
          "Contract (Art. 6(1)(b) UK GDPR): to accept and fulfil your order, dispatch drivers and provide customer service.",
          "Legitimate interest (Art. 6(1)(f)): to keep the App secure, prevent abuse and improve service quality.",
          "Legal obligation (Art. 6(1)(c)): to keep the accounting records required by HMRC and food safety authorities.",
          "Consent (Art. 6(1)(a)): for optional push notifications and location access on your device — you can revoke consent any time in your device settings.",
        ],
      },
      {
        h: "4. Third parties who process data on our behalf",
        list: [
          "OpenStreetMap Nominatim: to geocode delivery addresses (postcode → latitude/longitude).",
          "Resend: to send transactional email (order receipts, password resets).",
          "Emergent (hosting): to host the App's servers and databases within the EU/UK region.",
        ],
        p2: ["We never sell your data. We do not send marketing emails without your explicit opt-in."],
      },
      {
        h: "5. How long we keep your data",
        list: [
          "Account data: for as long as your account is active. On deletion, we anonymise your record within 30 days.",
          "Order records: 6 years, as required by UK tax law.",
          "Driver GPS pings: 30 days maximum, then aggregated and anonymised.",
          "Chat messages: for the lifetime of the associated order.",
        ],
      },
      {
        h: "6. Your rights under UK GDPR",
        p: ["You have the right to:"],
        list: [
          "Access the personal data we hold about you.",
          "Rectify inaccurate data.",
          'Erase your account and data ("right to be forgotten") — subject to legal record-keeping obligations.',
          "Restrict or object to processing.",
          "Data portability — receive your data in a common machine-readable format.",
          "Withdraw consent at any time for anything you consented to.",
        ],
        p2: ["Email Saiyeshaa4@gmail.com to exercise any of these rights. We will respond within one calendar month. If you are unsatisfied, you may complain to the UK Information Commissioner's Office (ICO) at ico.org.uk."],
      },
      {
        h: "7. Data security",
        p: ["Passwords are hashed with bcrypt. Session tokens are stored in the operating-system keychain. All traffic between the App and our servers is encrypted with TLS. Access to production databases is restricted to authorised personnel only."],
      },
      {
        h: "8. Children",
        p: ["The App is not directed at children under 13. We do not knowingly collect data from children. Please contact us if you believe we have."],
      },
      {
        h: "9. International transfers",
        p: ["Data is stored on servers located within the UK or European Economic Area. Where a processor operates outside these regions (e.g. some email provider infrastructure), we rely on the UK International Data Transfer Agreement and Standard Contractual Clauses."],
      },
      {
        h: "10. Changes to this policy",
        p: ['We may update this policy from time to time. When we do, the "Last updated" date at the top of this page will change. Substantial changes will be highlighted in-app the next time you sign in.'],
      },
      {
        h: "11. Contact us",
        p: ["Metros Franchise", "Metros Epsom, 100 Chessington Road, Epsom KT19 9UR, United Kingdom", "Email: Saiyeshaa4@gmail.com"],
      },
    ],
  },

  terms: {
    title: "Terms & Conditions",
    intro:
      'These Terms govern your use of the Metros Franchise takeaway ordering apps ("the App"). By creating an account or placing an order you agree to them.',
    sections: [
      { h: "1. Placing orders", p: ["Orders are accepted only during opening hours as shown in the App. Delivery is available only within the delivery window and delivery radius set by the shop. If we cannot fulfil an order we will contact you and refund any amount that may already have been taken."] },
      { h: "2. Prices & payment", p: ["Prices include VAT where applicable. Payment is due on delivery or collection — cash or card at the door / counter. We do not store any card details in the App."] },
      { h: "3. Cancellations", p: ['You can cancel an order at no charge until we mark it as "Preparing". Once preparation has started, we cannot refund the order because ingredients have already been used.'] },
      { h: "4. Allergens & food safety", p: ["Please tell staff about any allergies before ordering. Whilst we take reasonable care to prevent cross-contamination, our kitchen handles common allergens (gluten, nuts, dairy, eggs, sesame, shellfish, soya). We cannot guarantee any dish is 100% allergen-free."] },
      { h: "5. Delivery", p: ["ETAs shown in the App are estimates. Delivery drivers will attempt to hand the order to you at the address you provided. If nobody is available, the driver will hold the order for up to 10 minutes and then return it to the shop; a re-delivery charge may apply."] },
      { h: "6. Driver conduct", p: ["Drivers must hold a valid UK driving licence, appropriate insurance and a right to work in the UK. The shop reserves the right to suspend any driver account for misconduct or unsafe behaviour."] },
      { h: "7. Accounts", p: ["You are responsible for keeping your account credentials secure. You may not share your account. You may delete your account at any time from Profile → Account."] },
      { h: "8. Prohibited use", p: ["You must not attempt to disrupt the service, scrape data, place fraudulent orders or use the App for anything unlawful."] },
      { h: "9. Liability", p: ["To the maximum extent permitted by law, our liability for any single order is limited to the value of that order. Nothing in these Terms limits liability for death, personal injury or fraud caused by our negligence."] },
      { h: "10. Changes", p: ['We may update these Terms from time to time. The "Last updated" date will change and the new Terms will apply to orders placed after that date.'] },
      { h: "11. Contact", p: ["Metros Franchise", "Metros Epsom, 100 Chessington Road, Epsom KT19 9UR, United Kingdom", "Email: Saiyeshaa4@gmail.com"] },
    ],
  },

  cookies: {
    title: "Cookie Information",
    intro:
      'This Cookie Information explains how Metros Franchise ("we", "us") uses cookies and similar storage on our takeaway ordering website and apps ("the App"). We operate from Metros Epsom, 100 Chessington Road, Epsom KT19 9UR, United Kingdom.',
    sections: [
      { h: "1. What are cookies?", p: ["Cookies and similar technologies (such as local storage and secure device keychains) are small pieces of data stored on your device. They let the App remember your basket, keep you signed in and work reliably."] },
      {
        h: "2. The cookies we use",
        list: [
          "Essential — session tokens that keep you signed in and your basket saved. The App cannot function without these.",
          "Preferences — remembering your order type (delivery or collection) and similar choices.",
          "Security — protecting against fraudulent or abusive requests.",
        ],
      },
      { h: "3. Cookies we do not use", p: ["We do not use advertising, marketing or third-party tracking cookies, and we do not build advertising profiles about you."] },
      { h: "4. Managing cookies", p: ["Because our cookies are essential to placing orders, disabling them in your browser or device may stop the App from working. You can clear stored data at any time via your browser or device settings."] },
      { h: "5. Contact", p: ["Metros Franchise", "Metros Epsom, 100 Chessington Road, Epsom KT19 9UR, United Kingdom", "Email: Saiyeshaa4@gmail.com"] },
    ],
  },
};

const Para = ({ children }) => (
  <p className="text-muted-foreground mt-3 leading-relaxed">{children}</p>
);

export default function Legal() {
  const { page } = useParams();
  const c = CONTENT[page] || CONTENT.privacy;
  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-12" data-testid={`legal-${page || "privacy"}`}>
      <h1 className="font-heading font-extrabold text-3xl md:text-4xl">{c.title}</h1>
      <p className="text-xs uppercase tracking-widest text-metros-red font-bold mt-3">Last updated: {UPDATED}</p>
      {c.intro && <Para>{c.intro}</Para>}

      {c.sections.map((sec, i) => (
        <section key={i} className="mt-8">
          <h2 className="font-heading font-bold text-lg text-metros-black">{sec.h}</h2>
          {sec.p?.map((t, j) => <Para key={`p${j}`}>{t}</Para>)}
          {sec.list && (
            <ul className="mt-3 space-y-2 list-disc pl-5">
              {sec.list.map((t, k) => (
                <li key={`l${k}`} className="text-muted-foreground leading-relaxed">{t}</li>
              ))}
            </ul>
          )}
          {sec.p2?.map((t, j) => <Para key={`p2${j}`}>{t}</Para>)}
        </section>
      ))}

      <p className="text-sm text-metros-black font-semibold mt-10 pt-6 border-t border-gray-100">© 2026 Metros Franchise. All rights reserved.</p>
    </div>
  );
}
